// src/components/analytics/MetricsBoard.jsx
import { useEffect, useState } from 'react';
import axios from '../../../api/axiosInstance';
import Loader from '../components/Loader';
import ErrorBox from '../components/ErrorBox';
import dayjs from 'dayjs';
import { Chart as ChartJS, LineElement, PointElement, CategoryScale, LinearScale, Tooltip } from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(LineElement, PointElement, CategoryScale, LinearScale, Tooltip);

const METRICS_OPTIONS = [
  { key: 'profile_visits', label: 'Profile visits' },
  { key: 'flashes_activity', label: 'Flashes activity' },
  { key: 'trippins_activity', label: 'Trippins activity' },
  { key: 'reservation_clicks', label: 'Reservation clicks' },
  { key: 'completed_reservations', label: 'Completed reservations' },
  { key: 'event_views', label: 'Views on event posts' },
  { key: 'post_views', label: 'Post views' },
  { key: 'ad_spend', label: 'Ad money spent' },
];

export default function MetricsBoard({ businessId }) {
  const [selectedMetrics, setSelectedMetrics] = useState(['profile_visits']);
  const [period, setPeriod] = useState(7); // days
  const [format, setFormat] = useState('graph'); // or list

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    setLoading(true);
    setErr(null);

    axios
      .get('/dashboard/analytics', {
        params: {
          business_id: businessId,
          metrics: selectedMetrics.join(','),
          period,
        },
      })
      .then(({ data }) => setData(data))
      .catch(setErr)
      .finally(() => setLoading(false));
  }, [businessId, selectedMetrics, period]);

  /*────────── UI helpers ─────────*/
  const toggleMetric = (key) =>
    setSelectedMetrics((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );

  /*────────── Render ─────────*/
  if (loading) return <Loader />;
  //if (err) return <ErrorBox message="Failed to load analytics." />;
  if (err) return null;

  return (
    <section className="space-y-4 rounded-lg bg-white p-6 shadow">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <h2 className="text-lg font-semibold">Analytics</h2>

        {/* Metric multi-select */}
        <div className="flex flex-wrap gap-2">
          {METRICS_OPTIONS.map((m) => (
            <label key={m.key} className="flex items-center space-x-1 text-sm">
              <input
                type="checkbox"
                checked={selectedMetrics.includes(m.key)}
                onChange={() => toggleMetric(m.key)}
                className="rounded border-gray-300 text-blue-600 shadow-sm focus:ring-blue-500"
              />
              <span>{m.label}</span>
            </label>
          ))}
        </div>

        {/* Period */}
        <select
          value={period}
          onChange={(e) => setPeriod(Number(e.target.value))}
          className="rounded-md border-gray-300 text-sm shadow-sm focus:border-blue-600 focus:ring-blue-600"
        >
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
        </select>

        {/* Format */}
        <select
          value={format}
          onChange={(e) => setFormat(e.target.value)}
          className="rounded-md border-gray-300 text-sm shadow-sm focus:border-blue-600 focus:ring-blue-600"
        >
          <option value="graph">Graph</option>
          <option value="list">List</option>
        </select>
      </header>

      {/* Data display */}
      {format === 'graph' ? (
        <Line
          data={{
            labels: (data?.dates ?? []).map((d) => dayjs(d).format('MMM D')),
            datasets: selectedMetrics.map((m, idx) => ({
              label: METRICS_OPTIONS.find((x) => x.key === m)?.label,
              data: data?.metrics?.[m] ?? [],
              tension: 0.3,
              borderWidth: 2,
              pointRadius: 2,
            })),
          }}
          options={{ responsive: true, maintainAspectRatio: false }}
          className="h-80"
        />
      ) : (
        <ul className="divide-y divide-gray-200">
          {selectedMetrics.map((m) => (
            <li key={m} className="py-3">
              <strong>
                {METRICS_OPTIONS.find((x) => x.key === m)?.label}:
              </strong>{' '}
              {data?.totals?.[m] ?? 0}
            </li>
          ))}
        </ul>
      )}

      {/* Export */}
      <div className="flex justify-end">
        <a
          href={`/dashboard/export?business_id=${businessId}&metrics=${selectedMetrics.join(
            ',',
          )}&period=${period}`}
          className="rounded-md border border-gray-300 bg-white px-3 py-1 text-sm hover:bg-gray-100"
        >
          Export as CSV
        </a>
      </div>
    </section>
  );
}
